import Control from "sap/ui/core/Control";
import { MetadataOptions } from "sap/ui/core/Element";
import RenderManager from "sap/ui/core/RenderManager";
import hugerte, { Editor } from "hugerte";
import ThemeService from "ips/ac/base/service/ThemeService";
import TranslationUtil from "ips/ac/util/TranslationUtil";

/**
 * @namespace ips.ac.control
 */
export default class HugeRteEditor extends Control {

  static readonly metadata: MetadataOptions = {
    properties: {
      editorId: { type: "string", defaultValue: "" },
      value: { type: "string", defaultValue: "" },
      plugins: { type: "string", defaultValue: "" },
      toolbar: { type: "string", defaultValue: "" },
      isEditable: { type: "boolean", defaultValue: true },
      height: { type: "string", defaultValue: "300px" },
    },
    events: {},
  };

  private editor: Editor;

  /* =========================================================== */

  /* lifecycle methods
  /* =========================================================== */

  init() {
    const sEditorId = this.getProperty("editorId") || "hugerte_" + this.getId();
    if (!this.getProperty("editorId")) {
      this.setProperty("editorId", sEditorId);
    }
  }

  onAfterRendering() {
    this._destroyEditor();

    // Delay init to ensure DOM is ready and previous instance is purged
    setTimeout(() => {
      this._initEditor();
    }, 0);
  }

  exit() {
    this._destroyEditor();
  }

  /* =========================================================== */

  /* editor
  /* =========================================================== */
  _initEditor() {
    hugerte.baseURL = "resources/hugerte";

    hugerte.init({
      target: document.getElementById(this.getProperty("editorId")),
      height: this.getProperty("height"),
      menubar: false,
      plugins: this.getProperty("plugins"),
      toolbar: this.getProperty("toolbar"),
      // read by the checklist plugin (hugerte/plugins/checklist/plugin.js)
      checklist_label: TranslationUtil.getText("@HugeRteEditor_Checklist"),
      readonly: !this.getProperty("isEditable"),
      content_style: "@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap'); body { font-family: 'Inter', sans-serif; }",
      content_css: ThemeService.getInstance().isDarkMode() ? "dark" : "default",
      skin: ThemeService.getInstance().isDarkMode() ? "oxide-dark" : "oxide",
      setup: (editor) => {
        this.editor = editor;

        editor.on("init", () => {
          editor.setContent(this.getProperty("value"));
        });

        editor.on("change keyUp NodeChange", () => {
          const sContent = editor.getContent();
          this.setProperty("value", sContent, true);
        });

      },
    });
  }

  _destroyEditor() {
    const sEditorId = this.getProperty("editorId");
    if (sEditorId) {
      const oExistingEditor = hugerte.get(sEditorId);
      if (oExistingEditor) {
        oExistingEditor.remove();
        this.editor = null;
      }
    }
  }


  /* =========================================================== */
  /* rendering
  /* =========================================================== */
  renderer = {
    apiVersion: 4,
    render: (rm: RenderManager, control: HugeRteEditor) => {
      rm.openStart("div", control.getId() + "-wrapper");
      rm.style("width", "100%");
      rm.openEnd();

      rm.openStart("textarea", control.getProperty("editorId"));
      rm.style("height", control.getProperty("height"));
      rm.style("width", "100%");
      rm.openEnd();
      rm.close("textarea");

      rm.close("div");
    },
  };

  /* =========================================================== */

  /* setter
  /* =========================================================== */
  setIsEditable(isEditable: boolean): void {
    this.setProperty("isEditable", isEditable, true);
    if (this.editor) {
      this.editor.mode.set(isEditable ? "design" : "readonly");
    }
  }

  setValue(value: string): void {
    this.setProperty("value", value, true);
    if (this.editor) {
      if (!value) {
        value = "";
      }
      this.editor.setContent(value);
    }
  }
}